/* eslint-disable @tanstack/query/exhaustive-deps */
import {
  Client,
  CommercialType,
  EventExhibitorPack,
  EventSummary,
  EventWithCustomField,
  ExhibitorCommercialClientType,
  ExhibitorCommercialMeetingType,
  ExhibitorDirectoryFilters,
  ExhibitorDirectoryItem,
  ExponentStructureType,
  FilterClientType,
  MinimalStructureFormattedForList,
  MySalesHistory,
  PaginatedResponse,
  ParticipationRequestType,
  QueryArgs,
  StructureType,
  StructureTypeFormatted,
  Visitor,
} from "@ac-expo/api-types";
import { keepPreviousData, useInfiniteQuery, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios, publicAxios } from "../axiosInstances";
import { organiserExhibitorsQueryKeys } from "../query-keys/organiser.query-keys";
import structureQueryKeys, { structureClientsQueryKeys } from "../query-keys/structure.query-keys";

type MinimalEvent = {
  id: string;
  name: string;
};

const getUserStructure = async () => {
  const res = await privateAxios.get<StructureType>("/structures/mine");
  return res.data;
};

export const useGetUserStructureQuery = () => {
  return useQuery({
    queryKey: structureQueryKeys.structure(),
    queryFn: getUserStructure,
  });
};

const getUserCommercialStructure = async () => {
  const res = await privateAxios.get<StructureType>("/structures/commercial-mine");
  return res.data;
};

export const useGetUserCommercialStructureQuery = () => {
  return useQuery({
    queryKey: structureQueryKeys.structure(),
    queryFn: getUserCommercialStructure,
  });
};

const getStructure = async (id: string) => {
  const { data } = await privateAxios.get<StructureTypeFormatted>(`/structures/${id}`);
  return data;
};

export const useGetStructure = (id: string, options?: QueryArgs<StructureTypeFormatted>) => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.get(id),
    queryFn: () => getStructure(id),
    enabled: !!id,
    ...options,
  });
};

const getStructureByAdminId = async (id: string) => {
  const { data } = await privateAxios.get<StructureTypeFormatted>(`/structures/admin/${id}`);
  return data;
};

export const useGetStructureByAdminId = (
  id: string | undefined,
  enabled: boolean,
  options?: QueryArgs<StructureTypeFormatted, StructureTypeFormatted>,
) => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.getByAdminId(id!),
    queryFn: () => getStructureByAdminId(id!),
    enabled: !!id && enabled,
    ...options,
  });
};

export const useGetStructureProductsAndActivityFiltered = (id: string, eventId?: string | null) => {
  return useGetStructure(id, {
    select: (data) => ({
      ...data,
      products: eventId
        ? data.products.filter((product) => product.events.find((event) => event.id === eventId))
        : data.products,
      activities: eventId
        ? data.activities.filter((activity) => {
            return activity.eventId === eventId && activity.structureSchedule && activity.isOnline;
          })
        : data.activities.filter((activity) => {
            return activity.structureSchedule && activity.isOnline;
          }),
    }),
  });
};

const getStructureMinimalInformations = async (id: string, eventId?: string) => {
  const { data } = await privateAxios.get<ExponentStructureType>(
    `/structures/${id}/exponent-view`,
    {
      params: eventId ? { eventId } : {},
    },
  );
  return data;
};

export const useGetStructureMinimalInformations = (
  id: string,
  eventId?: string | null,
  options?: Omit<UseQueryOptions<ExponentStructureType>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    queryKey: structureQueryKeys.exponentView(id, eventId ?? undefined),
    queryFn: () => getStructureMinimalInformations(id, eventId ?? undefined),
    enabled: !!id,
    ...options,
  });
};

const getExhibitorPack = async (eventId: string, structureId: string) => {
  const { data } = await publicAxios.get<EventExhibitorPack>(
    `/events/${eventId}/exhibitors/${structureId}/pack`,
  );
  return data;
};

export const useGetExhibitorPackQuery = (eventId?: string | null, structureId?: string) => {
  return useQuery({
    queryKey: ["exhibitor-pack", eventId, structureId],
    queryFn: () => getExhibitorPack(eventId!, structureId!),
    enabled: !!eventId && !!structureId,
  });
};

const getStructureAsOrganiser = async (id: string) => {
  const { data } = await privateAxios.get<StructureTypeFormatted>(`/structures/${id}/as-organiser`);
  return data;
};

export const useGetStructureAsOrganiser = (id: string) => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.getAsOrganiser(id),
    queryFn: () => getStructureAsOrganiser(id),
    enabled: !!id,
  });
};

async function getRequestStatus(eventId: string) {
  try {
    const { data } = await privateAxios.get<ParticipationRequestType>(
      `/structures/request-participations/${eventId}`,
    );

    return data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response?.status === 404) {
        return null;
      }
      err.message = err.response?.data.message ?? err.message;
    }
    throw err;
  }
}

export function useGetRequestStatus(eventId: string, enabled?: boolean) {
  return useQuery({
    queryKey: structureQueryKeys.request(eventId),
    queryFn: () => getRequestStatus(eventId),
    enabled: enabled ?? true,
  });
}

async function getRequestParticipations() {
  const { data } = await privateAxios.get<ParticipationRequestType[]>(
    `/structures/request-participations`,
  );

  return data;
}

export function useGetRequestParticpations() {
  return useQuery({
    queryKey: structureQueryKeys.requestParticipations(),
    queryFn: () => getRequestParticipations(),
  });
}

const getStructureByConnectId = async (connectId: string) => {
  const { data } = await privateAxios.get<StructureType>(`/structures/connect/${connectId}`);
  return data;
};

export const useGetStructureByConnectId = (connectId: string) => {
  return useQuery({
    queryKey: organiserExhibitorsQueryKeys.getByConnectId(connectId),
    queryFn: () => getStructureByConnectId(connectId),
    enabled: !!connectId && connectId.length === 10,
  });
};

type GetStructuresFilters = {
  eventId?: string;
  searchText?: string;
  isScanned?: boolean;
  isLiked?: boolean;
  minimal?: boolean;
};

const getStructures = async <TFilters extends GetStructuresFilters>(
  filters: TFilters,
): Promise<
  TFilters extends { minimal: true } ? MinimalStructureFormattedForList[] : StructureTypeFormatted[]
> => {
  try {
    const res = await privateAxios.get<StructureTypeFormatted[]>(`/structures`, {
      params: filters,
    });
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message ?? error.message;
    }
    throw error;
  }
};

export const useGetStructures = <TFilters extends GetStructuresFilters>(filters: TFilters) => {
  return useQuery({
    queryKey: structureQueryKeys.list(filters),
    queryFn: () => getStructures(filters),
  });
};

const getStructureByCommercialId = async (commercialId: string | undefined) => {
  const { data } = await privateAxios.get<StructureType>(`/structures/commercial/${commercialId}`);
  return data;
};

export const useGetStructureByCommercialId = (commercialId: string | undefined) => {
  return useQuery({
    queryKey: structureQueryKeys.getByCommercialId(commercialId),
    queryFn: () => getStructureByCommercialId(commercialId),
    enabled: !!commercialId,
  });
};

const getEventsByStructureId = async (structureId: string) => {
  const { data } = await privateAxios.get<EventWithCustomField<EventSummary>[]>(
    `/structures/${structureId}/events`,
  );
  return data;
};

export const useGetEventsByStructureId = (
  structureId: string | undefined,
  options?: Omit<UseQueryOptions<EventWithCustomField<EventSummary>[]>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    queryFn: () => getEventsByStructureId(structureId!),
    queryKey: structureQueryKeys.getEvents(structureId!),
    enabled: !!structureId,
    ...options,
  });
};

const getCommercialsByStructureId = async (structureId: string) => {
  const { data } = await privateAxios.get<CommercialType[]>(
    `/structures/${structureId}/commercial`,
  );
  return data;
};

export const useGetCommercialsByStructureId = (structureId: string, eventId?: string) => {
  return useQuery({
    queryFn: () =>
      eventId
        ? getCommercialsByStructureIdOnEvent(structureId, eventId)
        : getCommercialsByStructureId(structureId),
    queryKey: structureQueryKeys.commercial(structureId, eventId ?? ""),
    enabled: !!structureId,
  });
};

type CommercialsOnEventFilters = { searchText?: string };

const getCommercialsByStructureIdOnEvent = async (
  structureId: string,
  eventId: string,
  filters?: CommercialsOnEventFilters,
): Promise<CommercialType[]> => {
  const { data } = await privateAxios.get<CommercialType[]>(
    `/structures/${structureId}/commercial/event/${eventId}`,
    { params: { searchText: filters?.searchText } },
  );
  return data;
};

export const useGetCommercialsByStructureIdOnEvent = (
  structureId: string | undefined,
  eventId: string,
  filters?: CommercialsOnEventFilters,
) =>
  useQuery({
    enabled: !!structureId,
    queryFn: () => getCommercialsByStructureIdOnEvent(structureId!, eventId, filters),
    queryKey: structureQueryKeys.commercial(structureId!, eventId, filters),
    placeholderData: keepPreviousData,
  });
export const getExhibitorVisitors = async (structureId: string, eventId?: string) => {
  try {
    const url =
      eventId && typeof eventId === "string"
        ? `/structures/${structureId}/visitors/${eventId}`
        : `/structures/${structureId}/visitors`;
    const { data } = await privateAxios.get<Visitor[]>(url);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message ?? error.message,
      };
    }
    throw error;
  }
};

export const useGetExhibitorVisitorsQuery = <TData = Visitor[]>(
  structureId?: string,
  eventId?: string,
  options?: QueryArgs<Visitor[], TData>,
) => {
  return useQuery({
    enabled: !!structureId,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know that structureId is defined
    queryKey: structureQueryKeys.visitors(structureId!, eventId),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know that structureId is defined
    queryFn: () => getExhibitorVisitors(structureId!, eventId),
    ...options,
  });
};

export const getExhibitorVisitorsPaginated = async (
  structureId: string,
  eventId?: string,
  filters?: {
    pagination?: { pageIndex: number; pageSize: number };
    sorting?: { id: string; desc: boolean }[];
    searchText?: string;
    includeScannedVisitors?: boolean;
  },
) => {
  try {
    const url = eventId
      ? `/structures/${structureId}/visitors/paginated/${eventId}`
      : `/structures/${structureId}/visitors/paginated`;
    const { data } = await privateAxios.get<PaginatedResponse<Visitor>>(url, {
      params: filters,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message ?? error.message,
      };
    }
    throw error;
  }
};

export const useGetExhibitorVisitorsPaginatedQuery = (
  structureId?: string,
  eventId?: string,
  filters?: {
    pagination?: { pageIndex: number; pageSize: number };
    sorting?: { id: string; desc: boolean }[];
    searchText?: string;
    includeScannedVisitors?: boolean;
  },
  options?: QueryArgs<PaginatedResponse<Visitor>>,
) => {
  return useQuery({
    enabled: !!structureId,
    queryKey: structureQueryKeys.visitorsPaginated(structureId!, eventId, filters),
    queryFn: () => getExhibitorVisitorsPaginated(structureId!, eventId, filters),
    ...options,
  });
};

export const getExhibitorVisitorsEvents = async (structureId: string) => {
  const { data } = await privateAxios.get<MinimalEvent[]>(
    "/structures/" + structureId + "/visitors/events",
  );
  return data as MinimalEvent[];
};

export const useGetExhibitorVisitorsEventsQuery = <TData = MinimalEvent[]>(
  structureId?: string,
  options?: QueryArgs<MinimalEvent[], TData>,
) => {
  return useQuery({
    enabled: !!structureId,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know that structureId is defined
    queryKey: structureQueryKeys.visitors(structureId!, "events"),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know that structureId is defined
    queryFn: () => getExhibitorVisitorsEvents(structureId!),
    ...options,
  });
};

export const getClientsOfMyStructure = async () => {
  const { data } = await privateAxios.get<Client[]>("/structures/clients");
  return data as Client[];
};

export const useGetClientsOfMyStructureQuery = <TData = Client[]>(
  options?: QueryArgs<Client[], TData>,
) => {
  return useQuery({
    queryKey: [],
    queryFn: () => getClientsOfMyStructure(),
    ...options,
  });
};

export const getCommercialClients = async (
  id: string,
  structureId: string,
  filters?: FilterClientType & {
    pagination?: { pageIndex: number; pageSize: number };
    sorting?: { id: string; desc: boolean }[];
  },
) => {
  const { data } = await privateAxios.get<PaginatedResponse<ExhibitorCommercialClientType>>(
    `/exhibitor-commercial/${structureId}/${id}/clients`,
    {
      params: filters,
    },
  );
  return data;
};

export const useGetCommercialClientsInfiniteQuery = (
  filters: FilterClientType & {
    pageSize: number;
    sorting?: { id: string; desc: boolean }[];
  },
  id: string,
  structureId?: string,
) => {
  return useInfiniteQuery({
    enabled: !!structureId && !!id,
    queryKey: structureClientsQueryKeys.byEventId(id, filters),
    queryFn: ({ pageParam = 0 }) =>
      getCommercialClients(id, structureId!, {
        ...filters,
        pagination: { pageIndex: pageParam, pageSize: filters.pageSize },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = Math.ceil(lastPage.count / filters.pageSize);
      const nextPage = allPages.length;
      return nextPage < totalPages ? nextPage : undefined;
    },
    initialPageParam: 0,
  });
};

const getCommercials = async () => {
  const res = await privateAxios.get<CommercialType[]>("/structures/commercials");
  return res.data;
};

export const useGetCommercials = () => {
  return useQuery({
    queryKey: structureQueryKeys.commercials,
    queryFn: getCommercials,
  });
};

const getStructureSalesHistory = async () => {
  try {
    const { data } = await privateAxios.get<MySalesHistory[]>("/structures/sales-history");
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useGetExhibitorSalesHistoryQuery = () => {
  return useQuery({
    queryKey: structureQueryKeys.salesHistory(),
    queryFn: getStructureSalesHistory,
    select: (data) =>
      data.map((sale) => ({
        ...sale,
        date: new Date(sale.date),
      })),
  });
};

const getStructureBySlug = async (slug: string) => {
  const { data } = await privateAxios.get<ExponentStructureType>(`/structures/by-slug/${slug}`);
  return data;
};

export const useGetStructureBySlug = (slug: string, options?: QueryArgs<ExponentStructureType>) => {
  return useQuery({
    queryKey: [...organiserExhibitorsQueryKeys.get(slug), "slug"],
    queryFn: () => getStructureBySlug(slug),
    enabled: !!slug,
    ...options,
  });
};

const getStructureMeetings = async () => {
  const { data } = await privateAxios.get<ExhibitorCommercialMeetingType[]>(`/structures/meetings`);
  return data;
};

export const useGetStructureMeetings = () => {
  return useQuery({
    queryKey: structureQueryKeys.meetings,
    queryFn: getStructureMeetings,
  });
};

const getExhibitorDirectory = async (filters?: ExhibitorDirectoryFilters) => {
  const { data } = await privateAxios.get<PaginatedResponse<ExhibitorDirectoryItem>>(
    "/structures/directory",
    { params: filters },
  );
  return data;
};

export const useGetExhibitorDirectoryQuery = (
  filters?: ExhibitorDirectoryFilters,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: structureQueryKeys.directory(filters),
    queryFn: () => getExhibitorDirectory(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled,
  });
};
